import { ContentCopy, Refresh } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import exclamationIcon from 'assets/icons/icon-exclamation.svg';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { amountFormatter } from 'utils/formatters';
import { useStoreSelector } from 'v3/data/store';
import { setPixQRCodePayment } from 'v3/data/store/installments/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import * as api from 'v3/services/api';
import { PixQRCodePaymentRequest } from 'v3/services/interfaces/request';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import {
  CloseBox,
  CloseIconStyled,
  Content,
  Icon,
  ImageStyled,
  RowCounter,
  ScrollableArea,
  StyledCodeBox,
  StyledDialog,
  StyledTimerBox,
  TextOpacity,
} from 'v3/shared/components/Modal/PixQRCodeModal/styles';
import { IPixQRCodeModalProps, PixModalState } from 'v3/shared/components/Modal/PixQRCodeModal/types';
import { CircularTimer } from 'v3/shared/components/Progress/ProgressCircle';
import { BodyLargeStyled } from 'v3/shared/typography/BodyLarge/styles';
import { BodyMediumRegularStyled } from 'v3/shared/typography/BodyMedium/styles';
import { BodySmallRegularStyled } from 'v3/shared/typography/BodySmall/styles';
import { HeadlineSmall } from 'v3/shared/typography/HeadlineSmall';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { TitleSmallStyled } from 'v3/shared/typography/TitleSmall/styles';

export const PixQRCodeModal: React.FC<IPixQRCodeModalProps> = ({ open, setOpenModal, setOpenQRCodeByEmail }) => {
  const { isMobile } = useBreakpoints();
  const theme = useTheme();
  const [modalState, setModalState] = useState<PixModalState>(PixModalState.LOADING);
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { pix } = useStoreSelector((store) => store.installments);
  const { itemsToPay } = useStoreSelector((store) => store.installments);

  const getPixQRCode = useCallback(async () => {
    setModalState(PixModalState.LOADING);

    try {
      if (itemsToPay.isNotEmpty) {
        const itemNumbers: number[] = itemsToPay.map((item) => item.number);
        const installmentsToPay: PixQRCodePaymentRequest = {
          numbersOfInstallments: itemNumbers,
        };
        const response = await api.generatePixQRCode(contractingKey, installmentsToPay);
        setPixQRCodePayment(response.data);
        setModalState(PixModalState.SUCCESS);
      }
    } catch (error) {
      console.error(error);
      setModalState(PixModalState.ERROR);
    }
  }, [contractingKey, itemsToPay]);

  useEffect(() => {
    if (open) {
      getPixQRCode();
    }
    return () => {
      if (!open) {
        setModalState(PixModalState.LOADING);
        setPixQRCodePayment({
          pixCode: '',
          qrCode: '',
          value: 0,
          expirationSeconds: 0,
        });
      }
    };
  }, [open, getPixQRCode, setPixQRCodePayment]);

  const handleCopyPixCode = () => {
    showSnackbar('Texto copiado com sucesso!', 'success', true);
    navigator.clipboard.writeText(pix.pixCode);
  };

  const handlePixExpiration = () => {
    setModalState(PixModalState.EXPIRED);
    setPixQRCodePayment({
      pixCode: '',
      qrCode: '',
      value: 0,
      expirationSeconds: 0,
    });
  };

  const handleGenerateNewPixCode = () => {
    getPixQRCode();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setPixQRCodePayment({
      pixCode: '',
      qrCode: '',
      value: 0,
      expirationSeconds: 0,
    });
  };

  const renderModalContent = () => {
    switch (modalState) {
      case PixModalState.LOADING:
        return null;

      case PixModalState.ERROR:
        return (
          <>
            <Icon src={exclamationIcon} alt="Alert" />
            <BodyMediumRegularStyled color={theme.palette.common.white} marginTop={'2rem'} align="center">
              Infelizmente não foi possível acessar a chave Pix
            </BodyMediumRegularStyled>
            <BodySmallRegularStyled color={theme.palette.common.white} marginTop={'2rem'} marginBottom={'2rem'}>
              Outra tentativa deverá ser realizada
            </BodySmallRegularStyled>
            <FlexButton text="Voltar para a página inicial" size="small" handleClick={handleCloseModal} />
          </>
        );

      case PixModalState.EXPIRED:
        return (
          <>
            <StyledTimerBox>
              <RowCounter>
                <CircularTimer totalSeconds={1} size={44} strokeWidth={4} />
                <TitleSmallStyled>A chave Pix expirou</TitleSmallStyled>
              </RowCounter>
            </StyledTimerBox>
            <TitleSmallStyled color={theme.palette.common.white}>
              Para gerar uma nova chave Pix, clique no botão abaixo.
            </TitleSmallStyled>
            <FlexButton
              text="Gerar nova chave Pix"
              size="small"
              startIcon={Refresh}
              handleClick={handleGenerateNewPixCode}
            />
          </>
        );

      case PixModalState.SUCCESS:
      default:
        return (
          <>
            <SubtitleLarge color={theme.palette.secondary.light}>
              Mostre o QR Code ou envie a chave PIX para o cliente realizar o pagamento agora
            </SubtitleLarge>
            <ScrollableArea>
              <StyledTimerBox>
                <RowCounter>
                  {pix.qrCode ? (
                    <CircularTimer
                      totalSeconds={pix.expirationSeconds}
                      size={44}
                      strokeWidth={4}
                      onTimerEnd={handlePixExpiration}
                    />
                  ) : null}
                  <TitleSmallStyled>Conclua o pagamento antes que a chave Pix expire</TitleSmallStyled>
                </RowCounter>
                <TextOpacity>
                  <TitleSmallStyled>
                    Aguarde até 1 minuto para atualizar o status do seu pagamento no sistema
                  </TitleSmallStyled>
                </TextOpacity>
              </StyledTimerBox>
              {pix.qrCode ? <ImageStyled src={pix.qrCode} alt="QR Code Pix" /> : null}
              <BodyLargeStyled color={theme.palette.common.white} marginTop={'1rem'}>
                Valor Total : {amountFormatter(pix.value)}
              </BodyLargeStyled>
              <StyledCodeBox>
                <TitleSmallStyled color={theme.palette.common.white}>
                  Utilize o código abaixo para efetuar o pagamento usando Pix Copia e Cola
                </TitleSmallStyled>
                <TextOpacity>
                  <TitleSmallStyled color={theme.palette.common.white}>{pix.pixCode}</TitleSmallStyled>
                </TextOpacity>
                <FlexButton text="Copiar código" size="small" startIcon={ContentCopy} handleClick={handleCopyPixCode} />
              </StyledCodeBox>
              <FlexButton
                text="Receber o QR Code por e-mail"
                handleClick={setOpenQRCodeByEmail}
                $backgroundColor="transparent"
              />
            </ScrollableArea>
          </>
        );
    }
  };

  return (
    <StyledDialog
      open={open && modalState !== PixModalState.LOADING}
      fullScreen={isMobile && ![PixModalState.ERROR, PixModalState.EXPIRED].includes(modalState)}
      onClose={handleCloseModal}
    >
      <Content>
        <CloseBox>
          <CloseIconStyled onClick={handleCloseModal} aria-label="Fechar modal" />
        </CloseBox>
        {modalState !== PixModalState.ERROR && (
          <>
            <HeadlineSmall color={theme.palette.common.white} alignSelf={'flex-start'}>
              Pagamento via PIX
            </HeadlineSmall>
          </>
        )}

        {renderModalContent()}
      </Content>
    </StyledDialog>
  );
};
